import { useEffect, useState } from "react";
import { api } from "../../api";
import { fmtDateTime } from "../../lib/format";
import { SectionHeading, StatusBadge, EmptyState } from "../../components/ui";

interface Row {
  id: string;
  kind: string;
  source: string | null;
  myaspUserId: string | null;
  email: string | null;
  scenarioType: string | null;
  scenarioCode: string | null;
  statusCode: number | null;
  amount: number | null;
  matchedMemberId: string | null;
  result: string;
  errorDetail: string | null;
  receivedAt: string;
}

// MyASP status_code の意味 (myasp.ts と一致させること)
const STATUS_LABELS: Record<number, string> = {
  1: "初回登録",
  2: "課金停止",
  3: "課金継続・再開",
  4: "解約",
};
const STATUS_TONE: Record<number, "success" | "warning" | "danger" | "neutral"> =
  {
    1: "success",
    2: "warning",
    3: "success",
    4: "neutral",
  };

const RESULT_TONE: Record<string, "success" | "warning" | "danger" | "neutral"> =
  {
    ok: "success",
    processed: "success",
    unmatched: "danger",
    duplicate: "neutral",
    processing: "warning",
    error: "danger",
  };

export function AdminMyaspLogsPage() {
  const [items, setItems] = useState<Row[]>([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = (query: string) => {
    setLoading(true);
    const path = query.trim()
      ? `/admin/myasp-logs?q=${encodeURIComponent(query.trim())}`
      : "/admin/myasp-logs";
    api
      .get<{ items: Row[] }>(path)
      .then((r) => setItems(r.items))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load("");
  }, []);

  return (
    <div className="space-y-4">
      <SectionHeading level={2}>MyASP Webhook ログ</SectionHeading>
      <p className="text-sm text-tora-ink/60">
        MyASP から受信した決済 webhook の履歴です。決済成功時に status=3（課金継続・再開）が
        届いているか、突合できず unmatched になっていないかを確認できます。
      </p>

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          load(q);
        }}
      >
        <input
          className="input flex-1"
          placeholder="メールアドレス または MyASP user_id で検索"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button className="btn-primary" type="submit">
          検索
        </button>
      </form>

      {loading ? (
        <p className="text-sm text-tora-ink/50">読み込み中…</p>
      ) : items.length === 0 ? (
        <EmptyState title="該当する webhook ログはありません" />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border border-black/10">
            <thead className="bg-black/5 text-left">
              <tr>
                <th className="p-2">受信時刻</th>
                <th className="p-2">種別</th>
                <th className="p-2">status</th>
                <th className="p-2">メール</th>
                <th className="p-2">user_id</th>
                <th className="p-2">金額</th>
                <th className="p-2">結果</th>
                <th className="p-2">詳細</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-black/10">
                  <td className="p-2 whitespace-nowrap">
                    {fmtDateTime(r.receivedAt)}
                  </td>
                  <td className="p-2">{r.kind}</td>
                  <td className="p-2">
                    {r.statusCode != null ? (
                      <StatusBadge tone={STATUS_TONE[r.statusCode] ?? "neutral"}>
                        {r.statusCode}
                        {STATUS_LABELS[r.statusCode]
                          ? `: ${STATUS_LABELS[r.statusCode]}`
                          : ""}
                      </StatusBadge>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="p-2 font-mono">{r.email ?? "-"}</td>
                  <td className="p-2 font-mono">{r.myaspUserId ?? "-"}</td>
                  <td className="p-2 tabular-nums">
                    {r.amount != null ? `¥${r.amount.toLocaleString()}` : "-"}
                  </td>
                  <td className="p-2">
                    <StatusBadge tone={RESULT_TONE[r.result] ?? "neutral"}>
                      {r.result}
                    </StatusBadge>
                  </td>
                  <td className="p-2 text-tora-ink/60">
                    {r.errorDetail ?? "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
